import { useEffect, useRef } from 'react';
import queryString from 'query-string';
import { useRouter } from '@funtech-int/rival-react';

import { useSearchParams } from '@/shared/hooks/useSearchParams';
import { QueryParameter } from '@/shared/constants/queryParams';
import { getRelativeUrlWithoutRegionLocale } from '@/shared/lib/locale/client';

import { cleanPlayingParams } from '../../feature-player/utils/clean-playing-params';
import { playingContentIdVar } from '../../player-manager/hooks/use-playing-content-id-var';
import { useStopPlayingContent } from '../../player-manager/helpers/stop-playing-content';
import { useStartPlayingContent } from '../../player-manager/hooks/useStartPlayingContent';

export const useStartOrStopPlayingContentOnQueryParamsChange = () => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const stopPlayingContent = useStopPlayingContent();
    const startContent = useStartPlayingContent();

    const prevUrlRef = useRef('');

    useEffect(() => {
        const initialUrl = router.serialize(router.location) + (searchParams.toString() ?? '');
        prevUrlRef.current = initialUrl;

        const unsubscribe = router.listen(() => {
            const currentUrl = router.serialize(router.location) + (searchParams.toString() ?? '');
            const playingContentId = playingContentIdVar.get();

            const [prevPathname = '', prevSearch = ''] = prevUrlRef.current.split('?');
            const [currentPathname = '', currentSearch = ''] = currentUrl.split('?');

            if (
                getRelativeUrlWithoutRegionLocale(currentPathname) !==
                getRelativeUrlWithoutRegionLocale(prevPathname)
            ) {
                prevUrlRef.current = currentUrl;
                return;
            }

            const prevQueryObject = prevSearch ? queryString.parse(prevSearch) : {};
            const currentQueryObject = currentSearch ? queryString.parse(currentSearch) : {};

            const hasWatchInCurrentQuery = QueryParameter.WATCH in currentQueryObject;
            const hasWatchInPrevQuery = QueryParameter.WATCH in prevQueryObject;

            const needStopPlayingContent =
                hasWatchInPrevQuery && !hasWatchInCurrentQuery && Boolean(playingContentId);

            if (needStopPlayingContent && typeof playingContentId === 'string') {
                cleanPlayingParams();
                stopPlayingContent();
                prevUrlRef.current = currentUrl;
                return;
            }

            const filmId = currentQueryObject[QueryParameter.REDIRECT_TO_FILM_PAGE];

            const needStartPlayingContent =
                hasWatchInCurrentQuery && !playingContentId && Boolean(filmId);

            if (needStartPlayingContent && typeof filmId === 'string') {
                startContent({
                    contentId: filmId,
                });
            }

            prevUrlRef.current = currentUrl;
        });

        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router, stopPlayingContent]);
};
